import type { UseMutationResult } from '@tanstack/react-query';

import type {
  AdPlatform,
  BrandGuideResponse,
  PutAdAccountsRequest,
  PutAdAccountsResponse,
  PutCompetitorsRequest,
  PutCompetitorsResponse,
} from '@/contracts/onboarding-session';
import { HttpError } from '@/features/onboarding/services/http-client';

import { COMPETITOR_FROM_CATALOG, COPY, competitorDetail, countLabel } from '../copy';
import { competitorIconUrl } from '../services/competitor-icon';
import { PANEL_CLICK_GUARD_MS } from '../hooks/useClickGuard';
import type { Answer, Question } from '../quiz/questions';
import type { DockSpec } from '../state/buildTranscript';
import type { BrandProfile } from '../state/session';
import { BrandProfileCard } from './BrandProfileCard';
import { ConfirmPanel } from './ConfirmPanel';
import { QuestionPanel } from './QuestionPanel';

type ConfirmAds = UseMutationResult<PutAdAccountsResponse, Error, { platform: AdPlatform; body: PutAdAccountsRequest }>;
type ConfirmCompetitors = UseMutationResult<PutCompetitorsResponse, Error, PutCompetitorsRequest>;

export interface DockRendererProps {
  /** Any dock except the website question, which is the composer's. */
  dock: Exclude<DockSpec, { kind: 'url' }>;
  onAnswer: (question: Question, value: Answer) => void;
  onSkip: (question: Question) => void;
  /** Show the question before the current one (prefilled). Offered from question 2 on, while the quiz is open. */
  onBack?: () => void;
  confirmAds: ConfirmAds;
  confirmCompetitors: ConfirmCompetitors;
  /** The brand guide as the chat has it so far: the brand profile card is filled from it. */
  brandGuide: { data: BrandGuideResponse | null; loading: boolean; failed: boolean };
  /** The session whose competitor list is docked: its rows read their icons through it. */
  sessionId?: string;
  /** The site the brand guide is built from, and whether the research (CI) job is still going (the profile card's pending fields). */
  site?: string;
  researchPending?: boolean;
  onConfirmBrandProfile: (profile: Omit<BrandProfile, 'confirmedAt'>) => void;
  /** Passed to every panel: clicks are ignored this long after one mounts. Tests turn it off with 0. */
  clickGuardMs?: number;
}

/** What a failed confirmation says: rate limit and a stale id get their own line, everything else the generic one. */
export function confirmErrorLine(error: unknown): string {
  const code = error instanceof HttpError ? error.envelope?.error?.code : undefined;
  if (code === 'rate_limited') return COPY.retryLater;
  if (code === 'unknown_ad_account' || code === 'unknown_competitor') return COPY.confirmUnknown;
  return COPY.confirmFailed;
}

/** The docked card at the bottom of the chat for the current step: a question, a confirmation or the brand profile. */
export function DockRenderer({
  sessionId,
  dock,
  onAnswer,
  onSkip,
  onBack,
  confirmAds,
  confirmCompetitors,
  brandGuide,
  site,
  researchPending = false,
  onConfirmBrandProfile,
  clickGuardMs = PANEL_CLICK_GUARD_MS,
}: DockRendererProps) {
  switch (dock.kind) {
    case 'question':
      return (
        <QuestionPanel
          key={dock.question.id}
          question={dock.question}
          title={dock.title}
          index={dock.index}
          total={dock.total}
          initial={dock.initial}
          onAnswer={(value) => onAnswer(dock.question, value)}
          onSkip={() => onSkip(dock.question)}
          // Back starts at question 2 (nothing is before the first).
          onBack={dock.index > 1 ? onBack : undefined}
          clickGuardMs={clickGuardMs}
        />
      );
    case 'adAccounts':
      return (
        <ConfirmPanel
          key={`ads:${dock.platform}`}
          title={dock.title}
          subtitle={dock.subtitle}
          items={dock.accounts.map((a) => ({
            id: a.accountId,
            label: a.name ?? a.accountId,
            hint: countLabel(a.adCount, 'ad', 'ads'),
            imageUrl: a.avatarUrl,
          }))}
          searching={dock.searching}
          busy={confirmAds.isPending}
          error={confirmAds.isError ? confirmErrorLine(confirmAds.error) : null}
          onConfirm={(accountIds) => confirmAds.mutate({ platform: dock.platform, body: { accountIds } })}
          onSkip={() => confirmAds.mutate({ platform: dock.platform, body: { skipped: true } })}
          clickGuardMs={clickGuardMs}
        />
      );
    case 'competitors':
      return (
        <ConfirmPanel
          key="competitors"
          title={dock.title}
          subtitle={dock.subtitle}
          items={dock.competitors.map((c) => ({
            id: c.brandId,
            label: c.name,
            hint: c.domain ?? undefined,
            imageUrl: c.imageUrl ?? (sessionId && c.domain ? competitorIconUrl(sessionId, c.brandId) : undefined),
            detail: competitorDetail(c) ?? undefined,
            note: c.fromCatalog === true ? COMPETITOR_FROM_CATALOG : undefined,
          }))}
          initialSelected={dock.competitors.slice(0, dock.max).map((c) => c.brandId)}
          max={dock.max}
          busy={confirmCompetitors.isPending}
          error={confirmCompetitors.isError ? confirmErrorLine(confirmCompetitors.error) : null}
          onConfirm={(brandIds) => confirmCompetitors.mutate({ brandIds })}
          onSkip={() => confirmCompetitors.mutate({ skipped: true })}
          clickGuardMs={clickGuardMs}
        />
      );
    case 'brandProfile':
      return (
        <BrandProfileCard
          key="brandProfile"
          brand={dock.brand}
          site={site}
          guide={brandGuide.data}
          loading={brandGuide.loading}
          failed={brandGuide.failed}
          researchPending={researchPending}
          onConfirm={onConfirmBrandProfile}
          clickGuardMs={clickGuardMs}
        />
      );
  }
}
